/**
 * ComparisonSelectorDrawer
 *
 * Thin wrapper around WorkoutPickerDrawer for selecting Workout B.
 * Manages GPS checkbox state and past comparisons lazy-loading.
 *
 * On "Start Comparison" the parent receives:
 *   onStart(source, snapToGpx, applyGpsCorrections)
 *   where source = { type: 'upload'|'myfile'|'community'|'pastComparison', … }
 */

import { useState, useEffect, useCallback } from 'react';
import { Box, Checkbox, FormControlLabel, Divider, IconButton, useTheme } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme';
import { getPastComparisonsForSession, deleteRunningComparison } from '../../api/runningApi';
import { getClubFiles } from '../../api/clubApi';
import WorkoutPickerDrawer from './WorkoutPickerDrawer';

const formatGap = (seconds) => {
  if (seconds == null) return '';
  const abs = Math.abs(Math.round(seconds));
  const m = Math.floor(abs / 60);
  const s = abs % 60;
  const sign = seconds > 0 ? '+' : '-';
  return `${sign}${m}:${String(s).padStart(2, '0')}`;
};


export default function ComparisonSelectorDrawer({
  open,
  onClose,
  onStart,            // (source, snapToGpx, applyGpsCorrections) => void
  hasRaceGpx,         // show snap checkbox only when base workout has a race GPX
  baseSessionId,      // for fetching past comparisons
  defaultSnapToGpx = false,
  defaultApplyGpsCorrections = true,
  clubId = null,      // when set, show a "Club" tab listing club files for comparison
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  const [snapToGpx, setSnapToGpx] = useState(defaultSnapToGpx);
  const [applyGpsCorrections, setApplyGpsCorrections] = useState(defaultApplyGpsCorrections);
  // Until the box is touched we send nothing, and B is analysed the way B's own
  // page analysed it. Forcing this checkbox's value onto B is what made its
  // moving time disagree with its own analysis.
  const [gpsTouched, setGpsTouched] = useState(false);
  const [pastComparisons, setPastComparisons] = useState([]);
  const [pastLoading, setPastLoading] = useState(false);
  const [pastLoaded, setPastLoaded] = useState(false);
  // Club files tab
  const [clubFiles, setClubFiles] = useState([]);
  const [clubFilesLoading, setClubFilesLoading] = useState(false);
  const [clubFilesLoaded, setClubFilesLoaded] = useState(false);
  // Track active tab so GPS checkboxes can be hidden for prediction sources
  const [activeTabId, setActiveTabId] = useState(null);

  const handleDeletePastComparison = useCallback(async (comparisonId, e) => {
    e.stopPropagation();
    try {
      await deleteRunningComparison(comparisonId);
      setPastComparisons(prev => prev.filter(c => c.id !== comparisonId));
    } catch {
      // silently ignore — WorkoutPickerDrawer doesn't expose setError externally
    }
  }, []);

  const pastCompColumns = [
    {
      field: 'label_b',
      headerName: t('workoutBHeader', 'Workout B'),
      flex: 2,
      valueGetter: (value, row) => value || `Comparison #${row.id}`,
    },
    {
      field: 'created_at',
      headerName: t('date', 'Date'),
      width: 100,
      valueFormatter: (value) => {
        if (!value) return '';
        try { return new Date(value).toLocaleDateString(); } catch { return value; }
      },
    },
    {
      field: 'total_distance_km',
      headerName: t('dist', 'Dist'),
      width: 90,
      valueFormatter: (value) => (value != null ? `${Number(value).toFixed(1)} km` : ''),
    },
    {
      field: 'gap_final_seconds',
      headerName: t('gap', 'Norm. Pace'),
      width: 90,
      valueFormatter: (value) => formatGap(value),
    },
    {
      field: '_delete',
      headerName: '',
      width: 40,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleDeletePastComparison(params.row.id, e)}
          sx={{ color: colors.redAccent[400] }}
          title={t('deleteComparison', 'Delete comparison')}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  // Reset checkboxes when drawer opens with new defaults
  useEffect(() => {
    if (open) {
      setSnapToGpx(defaultSnapToGpx);
      setApplyGpsCorrections(defaultApplyGpsCorrections);
    }
  }, [open, defaultSnapToGpx, defaultApplyGpsCorrections]);

  // Load past comparisons once per baseSessionId when drawer opens
  useEffect(() => {
    if (!open || !baseSessionId || pastLoaded) return;
    setPastLoading(true);
    getPastComparisonsForSession(baseSessionId)
      .then((data) => {
        setPastComparisons(Array.isArray(data) ? data : []);
        setPastLoaded(true);
      })
      .catch(() => {})
      .finally(() => setPastLoading(false));
  }, [open, baseSessionId, pastLoaded]);

  // Reset cache when baseSessionId changes
  useEffect(() => {
    setPastComparisons([]);
    setPastLoaded(false);
  }, [baseSessionId]);

  // Load club files when drawer opens (if clubId is set)
  useEffect(() => {
    if (!open || !clubId || clubFilesLoaded) return;
    setClubFilesLoading(true);
    getClubFiles(clubId)
      .then((data) => {
        setClubFiles(Array.isArray(data) ? data : []);
        setClubFilesLoaded(true);
      })
      .catch(() => {})
      .finally(() => setClubFilesLoading(false));
  }, [open, clubId, clubFilesLoaded]);

  // Reset club files cache when clubId changes
  useEffect(() => {
    setClubFiles([]);
    setClubFilesLoaded(false);
  }, [clubId]);

  const handleSelect = (source) => {
    onStart(source, snapToGpx, gpsTouched ? applyGpsCorrections : null);
  };

  const isPredictionTab = activeTabId === 'predictions' || activeTabId === 'clubPredictions';

  const footer = (
    <Box>
      {!isPredictionTab && hasRaceGpx && (
        <FormControlLabel
          control={
            <Checkbox
              checked={snapToGpx}
              onChange={(e) => setSnapToGpx(e.target.checked)}
              size="small"
              sx={{ color: colors.blueAccent[400], '&.Mui-checked': { color: colors.blueAccent[400] } }}
            />
          }
          label={t('snapBToGpx', "Snap B's GPS to race GPX")}
          sx={{ color: colors.grey[300], display: 'block', mb: 0.5 }}
        />
      )}
      {!isPredictionTab && (
        <FormControlLabel
          control={
            <Checkbox
              checked={applyGpsCorrections}
              onChange={(e) => { setApplyGpsCorrections(e.target.checked); setGpsTouched(true); }}
              size="small"
              sx={{ color: colors.blueAccent[400], '&.Mui-checked': { color: colors.blueAccent[400] } }}
            />
          }
          label={t('applyGpsCorrectionsB', 'Apply GPS corrections to B')}
          sx={{ color: colors.grey[300], display: 'block' }}
        />
      )}
      <Divider sx={{ mt: 1.5, borderColor: colors.grey[700] }} />
    </Box>
  );

  const fmtDist = (m) => m == null ? '—' : `${(m / 1000).toFixed(1)} km`;
  const fmtDur = (s) => {
    if (s == null) return '—';
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = Math.floor(s % 60);
    return h > 0
      ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
      : `${m}:${String(sec).padStart(2, '0')}`;
  };
  const fmtPace = (p) => {
    if (p == null) return '—';
    const m = Math.floor(p);
    const s = Math.round((p - m) * 60);
    return `${m}:${String(s).padStart(2, '0')} /km`;
  };
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString() : '—';

  const clubFileColumns = [
    { field: 'contributor_email', headerName: t('member', 'Member'), flex: 1, minWidth: 100 },
    { field: 'race_name', headerName: t('race', 'Race'), flex: 1, minWidth: 90, valueFormatter: (v) => v || '—' },
    { field: 'workout_date', headerName: t('date', 'Date'), width: 100, valueFormatter: (v) => fmtDate(v) },
    { field: 'total_distance', headerName: t('dist', 'Dist'), width: 80, valueFormatter: (v) => fmtDist(v) },
    { field: 'total_duration', headerName: t('time', 'Time'), width: 80, valueFormatter: (v) => fmtDur(v) },
    { field: 'avg_pace', headerName: t('pace', 'Pace'), width: 90, valueFormatter: (v) => fmtPace(v) },
  ];

  const extraTabs = [
    {
      id: 'pastComparisons',
      label: t('pastComparisons', 'Past Comparisons'),
      rows: pastComparisons,
      columns: pastCompColumns,
      loading: pastLoading,
      getSource: (row) => ({ type: 'pastComparison', comparisonId: row.id }),
    },
    ...(clubId ? [{
      id: 'clubFiles',
      label: t('comparisonClubFiles', 'Club Files'),
      rows: clubFiles,
      columns: clubFileColumns,
      loading: clubFilesLoading,
      // Use the underlying RunningFile ID (file_id) — backend comparison_file_id doesn't require ownership
      getSource: (row) => ({ type: 'myfile', fileId: row.file_id }),
    }] : []),
  ];

  return (
    <WorkoutPickerDrawer
      open={open}
      onClose={onClose}
      onSelect={handleSelect}
      onTabChange={setActiveTabId}
      title={t('compareWith', 'Compare with...')}
      tabs={['upload', 'myFiles', 'community', 'predictions', 'clubPredictions']}
      defaultTab="myFiles"
      extraTabs={extraTabs}
      footer={footer}
      actionLabel={t('startComparison', 'Start Comparison')}
    />
  );
}
